import type { MapDef } from './MapDef.js';

// V1.5 second polished map per docs/03_MVP_SCOPE.md V1.5 IN list.
//
// Identity (vs Foundry):
//   - Foundry is the "industrial corridor" — relays on a straight north/south
//     spine, mirrored cover walls, the mid lane invites a head-on squad
//     contest. The neutral camp lives smack in that corridor.
//   - Vault is the "diagonal vault" — relays sit slightly off-axis (north
//     biased to the LEFT of center, south biased to the RIGHT) so the
//     signal-link chain runs Core → near-relay → mid → far-relay → Core in
//     an S-curve rather than a straight line. Cover walls funnel attackers
//     through TWO flank lanes (left-of-mid and right-of-mid) instead of one
//     central corridor. The mid relay is still the neutral camp anchor, but
//     the player who pushes through the LEFT lane hits a different terrain
//     than the RIGHT lane.
//
// Map invariants (must hold for every V1.5+ map):
//   - 2000 × 2000 world (matches WORLD_WIDTH/WORLD_HEIGHT exported from
//     simulation/State.ts so the renderer / fog grid don't have to know
//     which map is loaded).
//   - 32-px tile, same as Foundry.
//   - Two cores (one per side); three control nodes (north / mid / south);
//     two pre-spawned Recons.
//   - Signal-link chain (V1.5 §11 Option A) must work end-to-end:
//     each Core within SIGNAL_LINK_RANGE_PX = 500 of its near relay; near
//     relay within 500 of mid; mid within 500 of the far relay; far relay
//     within 500 of the enemy Core. Owning all three relays connects them.
//     Vault distance check (180° rotation around (1000, 1000)):
//       Core 0 (1000, 220) ↔ north (900, 600):  √(100² + 380²) ≈ 393 ✓
//       north (900, 600)   ↔ mid (1000, 1000):  √(100² + 400²) ≈ 412 ✓
//       mid (1000, 1000)   ↔ south (1100, 1400):√(100² + 400²) ≈ 412 ✓
//       south (1100, 1400) ↔ Core 1 (1000, 1780):√(100² + 380²) ≈ 393 ✓
//       Core 0 (1000, 220) ↔ mid (1000, 1000):  780 ✗ (must hop through
//       north — preserves the §11 design intent: no lone-wolf mid claim).
//   - Camp anchor at the mid relay (1000, 1000) so the V1.5 PvE
//     encampment slots in unchanged.
//
// Authoring rules (same as Foundry):
//   - Stay clear of the 120 px capture radius around each relay.
//   - Stay clear of the 200 px build-slot ring around each Core.
//   - Stay clear of the 60 px around squad spawn points.
//   - Obstacles are positive-area rects; pathfinding bakes them via the
//     buildGrid floor/ceil rounding.
export const VAULT: MapDef = {
  id: 'vault',
  name: 'Vault',
  width: 2000,
  height: 2000,
  tilePx: 32,
  cores: [
    { ownerId: 0, pos: { x: 1000, y: 220 } },
    { ownerId: 1, pos: { x: 1000, y: 1780 } },
  ],
  controlNodes: [
    // Off-axis north/south relays create the S-curve link chain. Mid stays
    // dead-center so the camp anchor lines up with Foundry's.
    { name: 'north', pos: { x: 900, y: 600 } },
    { name: 'mid', pos: { x: 1000, y: 1000 } },
    { name: 'south', pos: { x: 1100, y: 1400 } },
  ],
  obstacles: [
    // Core-side flank walls — each Core has cover on the side opposite its
    // adjacent relay, so the flanking attacker doesn't see straight to the
    // Core after slipping past the near relay. Stay 200 px clear of the
    // build-slot ring around each Core.
    { x: 1240, y: 380, w: 200, h: 60 },   // P0 right shoulder (well past slot ring)
    { x: 560, y: 1540, w: 200, h: 60 },   // P1 left shoulder (mirror)

    // Diagonal "vault wall" pairs flanking the mid relay. Two parallel
    // rectangles per side define the LEFT and RIGHT lanes. Squads have to
    // commit to one lane to reach mid; flanking squads can swing wide.
    // Left lane (from P0's perspective heading south).
    { x: 540, y: 880, w: 180, h: 50 },
    { x: 540, y: 1080, w: 180, h: 50 },
    // Right lane (mirrored).
    { x: 1280, y: 880, w: 180, h: 50 },
    { x: 1280, y: 1080, w: 180, h: 50 },

    // Cover near each off-axis relay — the relay's "back side" gets a small
    // wall so a defender can shoot from cover but not turtle the radius.
    // Pulled outside the 120 px capture radius (north relay at (900, 600);
    // wall at y=740 keeps the closest corner ≈ 143 px from the relay).
    { x: 760, y: 740, w: 80, h: 40 },     // behind north relay
    { x: 1160, y: 1220, w: 80, h: 40 },   // behind south relay (mirror)

    // Sideline rocks for Engineer mini-turret nests (Foundry has these too).
    // Spaced well clear of any relay's capture radius and the build-slot
    // rings; the lane walls already break central sightlines, so we don't
    // need a mid diagonal here (it intrudes on the camp's pickup radius).
    { x: 480, y: 540, w: 60, h: 60 },
    { x: 1460, y: 1400, w: 60, h: 60 },
  ],
  spawns: [
    { ownerId: 0, pos: { x: 920, y: 300 } },
    { ownerId: 1, pos: { x: 1080, y: 1700 } },
  ],
  neutralCampAnchor: { x: 1000, y: 1000 },
};
